/**
 * Exécute la machine à états : applique les événements, joue les effets
 * (envois, timers, calibration…) et notifie l'UI. Dépendances injectées :
 * testable avec une fausse horloge et un faux réseau.
 */
import type { Message } from './protocol';
import { step, type Effect, type GameEvent, type GameState, type TimerId } from './stateMachine';

export interface RuntimeDeps {
  send(message: Message): void;
  /** Programme `fn` dans `ms` ; renvoie une fonction d'annulation. */
  schedule(ms: number, fn: () => void): () => void;
  startCalibration(): void;
  startHandTracking?(): void;
  stopHandTracking?(): void;
  sendReplay?(round: number): void;
  /** Chifoumi : lire le geste tenu au MI et répondre par `localThrow`. */
  lockThrow?(round: number): void;
  /** Imite ce smiley : donner mon meilleur score (répondre par `localImitation`). */
  submitImitation?(round: number): void;
}

export type StateListener = (state: GameState, previous: GameState) => void;

export interface GameRuntime {
  readonly state: GameState;
  /** Réentrant : un événement émis pendant un traitement est mis en file. */
  dispatch(event: GameEvent): void;
  subscribe(listener: StateListener): () => void;
  dispose(): void;
}

export function createGameRuntime(initial: GameState, deps: RuntimeDeps): GameRuntime {
  let state = initial;
  let disposed = false;
  let busy = false;
  const queue: GameEvent[] = [];
  const timers = new Map<TimerId, () => void>();
  const listeners = new Set<StateListener>();

  const cancel = (id: TimerId): void => {
    timers.get(id)?.();
    timers.delete(id);
  };

  const run = (effect: Effect): void => {
    switch (effect.type) {
      case 'send':
        deps.send(effect.message);
        return;
      case 'startTimer': {
        cancel(effect.timer);
        const { timer, round } = effect;
        timers.set(
          timer,
          deps.schedule(effect.ms, () => {
            timers.delete(timer);
            dispatch({ type: 'timeout', timer, round });
          }),
        );
        return;
      }
      case 'cancelTimer':
        cancel(effect.timer);
        return;
      case 'startCalibration':
        deps.startCalibration();
        return;
      case 'startHandTracking':
        deps.startHandTracking?.();
        return;
      case 'stopHandTracking':
        deps.stopHandTracking?.();
        return;
      case 'sendReplay':
        deps.sendReplay?.(effect.round);
        return;
      case 'lockThrow':
        deps.lockThrow?.(effect.round);
        return;
      case 'submitImitation':
        deps.submitImitation?.(effect.round);
        return;
    }
  };

  const process = (event: GameEvent): void => {
    const previous = state;
    const result = step(state, event);
    state = result.state;
    for (const effect of result.effects) run(effect);
    if (state !== previous) for (const l of listeners) l(state, previous);
  };

  function dispatch(event: GameEvent): void {
    if (disposed) return;
    queue.push(event);
    if (busy) return;
    busy = true;
    try {
      // `dispose()` peut être appelé pendant un traitement (par un abonné).
      const isDisposed = (): boolean => disposed;
      for (let e = queue.shift(); e && !isDisposed(); e = queue.shift()) process(e);
    } finally {
      busy = false;
      queue.length = 0;
    }
  }

  return {
    get state() {
      return state;
    },
    dispatch,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    dispose() {
      disposed = true;
      for (const id of [...timers.keys()]) cancel(id);
      listeners.clear();
    },
  };
}
